import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DateTime } from 'luxon';
import { Observable, of, Subject, switchMap, tap } from 'rxjs';
import { LocalAuthService } from '../auth/local-auth.service';
import { ConfigService } from '../config.service';
import { ApiKeyService } from './api-key.service';
import { Ptrip, UiSafeStaff } from './commonInterfaces';
import { DataTransform, DateField } from './dataTransformFunctions';
import { Contact } from './types/contacts';
import { PotentialTripSearchRequest, PotentialTripSearchResponse, PotentialTripSearchResult } from './types';

export interface PotentialTripUpdatedArgs {
  id: number,
  operation: "added" | "updated" | "removed"
}

@Injectable({
  providedIn: 'root'
})
export class PotentialTripService {


  private potentialTripUpdated = new Subject<PotentialTripUpdatedArgs>()
  potentialTripUpdated$ = this.potentialTripUpdated.asObservable()

  constructor(
    private http: HttpClient,
    private config: ConfigService,
  ) { }

  readonly pTripDateFields: DateField<Ptrip>[] = [
    { dateType: "local", fieldName: "tripStart" },
    { dateType: "local", fieldName: "tripEnd" },
    { dateType: "local", fieldName: "regStart" },
    { dateType: "local", fieldName: "regEnd" },
  ]

  getForCustomer(customerId: number) {
    return this.http.get<Ptrip[]>(`${this.config.dataApiUrl}/customers/${customerId.toString()}/potential-trips`).pipe(
      switchMap(pTrips => {
        return DataTransform.replaceDatesArray<Ptrip>(pTrips, this.pTripDateFields)
      })
    )
  }

  searchPotentialTrips(payload: PotentialTripSearchRequest): Observable<PotentialTripSearchResponse> {
    return this.http.post<PotentialTripSearchResponse>(`${this.config.dataApiUrl}/recruiting/potential-trips/search`, payload).pipe(
      switchMap(data => {
        data.trips = DataTransform.replaceDatesArrayDirect(data.trips, [
          { dateType: "local", fieldName: "tripstart" },
          { dateType: "local", fieldName: "lastCall" },
        ]);
        return of(data);
      })
    );
  }
  
  createPotentialTrip(customerId: number, values: Partial<PotentialTripFormValue>) {

    const data: Partial<Ptrip> = {
      recruiterId: values.recruiter?.id ?? 0,
      assistId: values.assist?.id ?? 0,
      custId: customerId,
      contactId: values.contact?.id ?? 0,
      location1: values.primaryLocation ?? '0',
      location2: values.location2 ?? '0',
      location3: values.location3 ?? '0',
      location4: values.location4 ?? '0',
      location5: values.location5 ?? '0',
      tripStart: DateTime.fromJSDate(values.tripWindowStart ?? new Date()).setZone('utc', {keepLocalTime: true}).toJSDate(),
      tripEnd: DateTime.fromJSDate(values.tripWindowEnd ?? new Date()).setZone('utc', {keepLocalTime: true}).toJSDate(),
      regStart: DateTime.fromJSDate(values.regWindowStart ?? new Date()).setZone('utc', {keepLocalTime: true}).toJSDate(),
      regEnd: DateTime.fromJSDate(values.regWindowEnd ?? new Date()).setZone('utc', {keepLocalTime: true}).toJSDate(),
      status: values.status ?? 0,
      attendees: values.attendees ?? 0,
      cost: values.cost ?? 0,
      duration: values.duration ?? 7,
      hurdles: values.hurdles ? values.hurdles.join(",") : '',
      quickNote: values.quickNote ?? '',
      renewal: values.renewal ?? 0,
      closed: values.closed ?? 0


    }

    return this.http.post(`${this.config.dataApiUrl}/potential-trips`, data).pipe(
      tap(result => {
        this.potentialTripUpdated.next({ id: 0, operation: "added" })
      })
    )
  }

  
  editPotentialTrip(potentialTripId: number, customerId: number, values: Partial<PotentialTripFormValue>) {

    const data: Partial<Ptrip> = {
      
      recruiterId: values.recruiter?.id ?? 0,
      assistId: values.assist?.id ?? 0,
      custId: customerId,
      contactId: values.contact?.id ?? 0,
      location1: values.primaryLocation ?? '0',
      location2: values.location2 ?? '0',
      location3: values.location3 ?? '0',
      location4: values.location4 ?? '0',
      location5: values.location5 ?? '0',
      tripStart: DateTime.fromJSDate(values.tripWindowStart ?? new Date()).setZone('utc', {keepLocalTime: true}).toJSDate(),
      tripEnd: DateTime.fromJSDate(values.tripWindowEnd ?? new Date()).setZone('utc', {keepLocalTime: true}).toJSDate(),
      regStart: DateTime.fromJSDate(values.regWindowStart ?? new Date()).setZone('utc', {keepLocalTime: true}).toJSDate(),
      regEnd: DateTime.fromJSDate(values.regWindowEnd ?? new Date()).setZone('utc', {keepLocalTime: true}).toJSDate(),
      status: values.status ?? 0,
      attendees: values.attendees ?? 0,
      cost: values.cost ?? 0,
      duration: values.duration ?? 7,
      hurdles: values.hurdles ? values.hurdles.join(",") : '',
      quickNote: values.quickNote ?? '',
      renewal: values.renewal ?? 0,
      closed: values.closed ?? 0,
    }

    return this.http.put(`${this.config.dataApiUrl}/potential-trips/${potentialTripId.toString()}`, data).pipe(
      tap(result => {
        this.potentialTripUpdated.next({id: potentialTripId, operation: "updated"})
      })
    )

  }

  
  deletePotentialTrip(potentialTripId: number) {

    return this.http.delete(`${this.config.dataApiUrl}/potential-trips/${potentialTripId.toString()}`).pipe(
      tap(result => {
        this.potentialTripUpdated.next({ id: potentialTripId, operation: "removed" })
      })
    )
  }


getForm() {
  return new FormGroup({
    recruiter: new FormControl<UiSafeStaff | null>(null),
    assist: new FormControl<UiSafeStaff | null>(null),
    primaryLocation: new FormControl<string>('', { validators: [Validators.required] }),
    status: new FormControl<number>(30),
    tripWindowStart: new FormControl<Date>(new Date(), {validators: [Validators.required]}),
    tripWindowEnd: new FormControl<Date>(new Date(), {validators: [Validators.required]}),
    regWindowStart: new FormControl<Date>(new Date(), {validators: [Validators.required]}),
    regWindowEnd: new FormControl<Date>(new Date(), {validators: [Validators.required]}),
    attendees: new FormControl<number>(0),
    cost: new FormControl<number>(0),
    duration: new FormControl<number>(0),
    renewal: new FormControl<number>(0),
    contact: new FormControl<Contact | null>(null),
    hurdles: new FormControl<string[]>([]),
    location2: new FormControl<string>(''),
    location3: new FormControl<string>(''),
    location4: new FormControl<string>(''),
    location5: new FormControl<string>(''),
    quickNote: new FormControl<string>(''),
    closed: new FormControl<number>(0)

  })
}

getStatusString(status: number) {
  switch (status) {
    case 0:
      return "Didn't Register"
    case 30:
      return "Interested"
    case 50:
      return "Under Review"
    case 75:
      return "Verbally Committed"
    case 100:
      return "Registered"
    default:
      return "Unknown Status"

  }
}

}

type PotentialTripFormValue = {
  recruiter: UiSafeStaff | null
  assist: UiSafeStaff | null
  primaryLocation: string | null,
  status: number | null,
  tripWindowStart: Date | null,
  tripWindowEnd: Date | null,
  regWindowStart: Date | null,
  regWindowEnd: Date | null,
  attendees: number | null,
  cost: number | null,
  duration: number | null,
  renewal: number | null,
  contact: Contact | null,
  hurdles: string[] | null,
  location2: string | null,
  location3: string | null,
  location4: string | null,
  location5: string | null,
  quickNote: string | null,
  closed: number | null
}
